import React, { lazy, useState } from 'react';

/**
 * Drop-in replacement for `React.lazy` that recovers from `ChunkLoadError`.
 *
 * After a deploy, hashed chunk filenames change. Any tab that loaded the
 * previous `index.html` still references the old chunk hashes and will hit
 * a 404 (`ChunkLoadError: Loading chunk N failed`).  When that happens we
 * force a single hard reload so the browser fetches the new index.html and
 * the matching new chunk hashes.
 *
 * Safety: we only auto-reload **once per browser session**, tracked in
 * `sessionStorage`. If the reload still fails (e.g. the chunk really is
 * missing or there is a network problem), the error propagates to the
 * ErrorBoundary instead of looping forever.
 *
 * The returned component also has `preload()`. Once its chunk has loaded it
 * renders synchronously, without suspending — index.js relies on this to swap
 * prerendered HTML for the live page in one commit.
 *
 * @param {() => Promise<{ default: React.ComponentType<any> }>} factory
 *   Same signature as the argument to `React.lazy`.
 */
const RELOAD_KEY = 'chunk-load-reload-attempted';

function isChunkLoadError(err) {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const msg = String(err.message || '');
  return (
    /Loading chunk [\d]+ failed/i.test(msg) ||
    /Loading CSS chunk [\d]+ failed/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg)
  );
}

async function loadWithRetry(factory) {
  try {
    const mod = await factory();
    // Successful load — clear the reload guard so subsequent failures
    // (e.g. after the next deploy) get one fresh recovery attempt.
    try { window.sessionStorage.removeItem(RELOAD_KEY); } catch (_) {}
    return mod;
  } catch (err) {
    if (!isChunkLoadError(err)) throw err;

    let alreadyTried = false;
    try { alreadyTried = window.sessionStorage.getItem(RELOAD_KEY) === '1'; } catch (_) {}

    if (alreadyTried) {
      // Reload didn't fix it — give up and let ErrorBoundary render the UI.
      throw err;
    }

    try { window.sessionStorage.setItem(RELOAD_KEY, '1'); } catch (_) {}
    window.location.reload();
    // Never-resolving promise so React stays in the Suspense fallback
    // (a spinner) until the reload actually happens.
    return new Promise(() => {});
  }
}

export default function lazyWithRetry(factory) {
  let Loaded = null;
  let pending = null;
  const load = () => {
    if (!pending) {
      pending = loadWithRetry(factory).then((mod) => {
        Loaded = mod.default;
        return mod;
      });
    }
    return pending;
  };
  const Lazy = lazy(load);

  function LazyWithRetry(props) {
    // Fixed per mount, so a re-render after the chunk arrives can't swap the
    // component type (which would remount the page and drop its state).
    const [Component] = useState(() => Loaded || Lazy);
    return <Component {...props} />;
  }
  LazyWithRetry.preload = load;
  return LazyWithRetry;
}
